import * as browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { addCohortAssignmentsSearchParam, addExperimentsSearchParams, createSafeOriginUrl, dismissCommand, getBehavior, getCommand, isCommandExpired, } from "@eyeo-fragments/in-product-messaging/background";
import * as logger from "../../utilities/background";
import { getSettings, settings } from "../../prefs/background/settings";
import { CreationMethod, isNewTabBehavior, setNewTabCommandHandler } from "./middleware";
import { blockCountQueryParameter, lastShownKey, coolDownPeriodKey } from "./tab-manager.types";
import { Prefs } from "../../alias/prefs";
const onCreatedHandlerByIPMids = new Map();
const onUpdatedHandlerByIPMids = new Map();
let waitingTabs = {};
const tabIds = new Set();
const onNewTabUpdateHandlerByIpmId = new Map();
const createdTabsMap = new Map();
const serverLogger = new ewe.telemetry.ServerLogger("new_tab");
let openNewTabQueue = Promise.resolve();
export function compareNewTabRequestsByPriority(newTabA, newTabB) {
    if (newTabA.behavior.method === CreationMethod.force &&
        newTabB.behavior.method !== CreationMethod.force) {
        return -1;
    }
    if (newTabA.behavior.method !== CreationMethod.force &&
        newTabB.behavior.method === CreationMethod.force) {
        return 1;
    }
    if (newTabA.behavior.priority > newTabB.behavior.priority) {
        return -1;
    }
    if (newTabA.behavior.priority < newTabB.behavior.priority) {
        return 1;
    }
    if (newTabA.ipmId < newTabB.ipmId) {
        return -1;
    }
    if (newTabA.ipmId > newTabB.ipmId) {
        return 1;
    }
    return 0;
}
function dismissNewtabCommand(ipmId) {
    dismissCommand(ipmId);
    delete waitingTabs[ipmId];
}
export async function isCoolDownPeriodOngoing() {
    await Prefs.untilLoaded;
    return Prefs.get(lastShownKey) + Prefs.get(coolDownPeriodKey) > Date.now();
}
const openNewtabOnUpdated = (tabId, changeInfo, tab) => {
    if (changeInfo.status !== "complete" ||
        tab === null ||
        tabId !== tab.id ||
        !createdTabsMap.has(tabId)) {
        return;
    }
    const ipmId = createdTabsMap.get(tabId);
    if (typeof ipmId !== "string") {
        return;
    }
    const onUpdatedHandler = onNewTabUpdateHandlerByIpmId.get(ipmId);
    onNewTabUpdateHandlerByIpmId.delete(ipmId);
    browser.tabs.onUpdated.removeListener(onUpdatedHandler);
    serverLogger.behavior("tab_loaded", ipmId, ipmId);
};
async function addBlockCountToURL(urlString) {
    let url;
    try {
        url = new URL(urlString);
    }
    catch (_) {
        return null;
    }
    await Prefs.untilLoaded;
    const blockCount = Prefs.get("blocked_total");
    url.searchParams.append(blockCountQueryParameter, blockCount);
    return url.toString();
}
export async function buildTargetUrl(targetUrl) {
    let url = await addExperimentsSearchParams(targetUrl);
    if (url === null) {
        url = targetUrl;
    }
    url = await addCohortAssignmentsSearchParam(url);
    if (url === null) {
        url = targetUrl;
    }
    return url;
}
async function openNewtab() {
    logger.debug("[new-tab]:openNewtab");
    const candidates = Object.values(waitingTabs).sort(compareNewTabRequestsByPriority);
    for (const candidate of candidates) {
        logger.debug("[new-tab]:openNewtab");
        const { ipmId } = candidate;
        removeListeners(ipmId);
        if (await isCoolDownPeriodOngoing()) {
            logger.debug("[new-tab]: Cool down period still ongoing");
            return;
        }
        const behavior = getBehavior(ipmId);
        if (!isNewTabBehavior(behavior)) {
            logger.debug("[new-tab]: No command behavior");
            dismissNewtabCommand(ipmId);
            continue;
        }
        const targetUrl = createSafeOriginUrl(behavior.target);
        if (!targetUrl) {
            logger.debug("[new-tab]: Invalid target URL.");
            dismissNewtabCommand(ipmId);
            continue;
        }
        let url = await buildTargetUrl(targetUrl);
        url = await addBlockCountToURL(url);
        if (url === null) {
            logger.debug("[new-tab]: Invalid target URL.");
            dismissNewtabCommand(ipmId);
            continue;
        }
        const command = getCommand(ipmId);
        if (!command) {
            continue;
        }
        if (isCommandExpired(command)) {
            logger.error("[new-tab]: Command has expired.");
            dismissNewtabCommand(ipmId);
            continue;
        }
        const loadEventListener = openNewtabOnUpdated.bind(null);
        browser.tabs.onUpdated.addListener(loadEventListener);
        let tabId;
        try {
            const tab = await browser.tabs.create({ url });
            tabId = tab.id;
        }
        catch (ex) {
            logger.error(`[new-tab]: Tab creation error for IPM id "${ipmId}"`, ex);
            continue;
        }
        if (typeof tabId === "undefined") {
            logger.error(`[new-tab]: Invalid ID "${tabId}" for created tab for IPM ID "${ipmId}"`);
            continue;
        }
        createdTabsMap.set(tabId, ipmId);
        onNewTabUpdateHandlerByIpmId.set(ipmId, loadEventListener);
        await Prefs.set(lastShownKey, Date.now());
        serverLogger.behavior("tab_created", ipmId, ipmId);
        dismissNewtabCommand(ipmId);
    }
}
async function queueOpenNewtab() {
    const attempt = openNewTabQueue.then(async () => {
        await openNewtab();
    });
    openNewTabQueue = attempt.catch((ex) => {
        logger.reportAndLogError(ex, "[new-tab]: Error while opening new tab");
    });
    await openNewTabQueue;
}
function removeListeners(ipmId) {
    const onCreatedHandler = onCreatedHandlerByIPMids.get(ipmId);
    onCreatedHandlerByIPMids.delete(ipmId);
    const onUpdatedHandler = onUpdatedHandlerByIPMids.get(ipmId);
    onUpdatedHandlerByIPMids.delete(ipmId);
    if (typeof onCreatedHandler !== "undefined") {
        browser.tabs.onCreated.removeListener(onCreatedHandler);
        browser.tabs.onUpdated.removeListener(onUpdatedHandler);
    }
    browser.tabs.onRemoved.removeListener(onRemoved);
}
const openNotificationTab = () => {
    tabIds.clear();
    void queueOpenNewtab();
};
const onCreated = (tab) => {
    if (tab.url === "about:newtab") {
        openNotificationTab();
        return;
    }
    if (typeof tab.id !== "number") {
        return;
    }
    tabIds.add(tab.id);
};
const onRemoved = (tabId) => {
    createdTabsMap.delete(tabId);
    tabIds.delete(tabId);
};
const onUpdated = (tabId, changeInfo, tab) => {
    if (!tabIds.has(tabId) || changeInfo.status !== "complete") {
        return;
    }
    tabIds.delete(tabId);
    if (tab.url && /^https?:/.test(tab.url)) {
        return;
    }
    openNotificationTab();
};
async function handleCommand(ipmId) {
    logger.debug("[new-tab]:tab manager handleCommand", ipmId);
    const { installType } = await browser.management.getSelf();
    if (installType === "admin") {
        dismissNewtabCommand(ipmId);
        return;
    }
    await settings.onload();
    if (getSettings().suppress_update_page) {
        logger.debug("[new-tab]: suppress_update_page - true");
        dismissNewtabCommand(ipmId);
        return;
    }
    await Prefs.untilLoaded;
    if (Prefs.get("marketing_opt_out")) {
        logger.debug("[new-tab]: marketing_opt_out - true");
        dismissNewtabCommand(ipmId);
        return;
    }
    logger.debug("[new-tab]:openNewtab");
    const behavior = getBehavior(ipmId);
    if (!isNewTabBehavior(behavior)) {
        logger.debug("[new-tab]: Invalid command behavior");
        dismissNewtabCommand(ipmId);
        return;
    }
    waitingTabs[ipmId] = { behavior, ipmId };
    serverLogger.behavior("command_ready", ipmId, ipmId);
    if (behavior.method === CreationMethod.force) {
        return;
    }
    const onCreatedHandler = onCreated.bind(null);
    onCreatedHandlerByIPMids.set(ipmId, onCreatedHandler);
    const onUpdatedHandler = onUpdated.bind(null);
    onUpdatedHandlerByIPMids.set(ipmId, onUpdatedHandler);
    browser.tabs.onCreated.addListener(onCreatedHandler);
    browser.tabs.onRemoved.addListener(onRemoved);
    browser.tabs.onUpdated.addListener(onUpdatedHandler);
}
async function onCommandsProcessed() {
    if (Array.from(Object.values(waitingTabs)).some((candidate) => candidate.behavior.method === CreationMethod.force)) {
        await queueOpenNewtab();
    }
}
async function clearCommands() {
    for (const ipmId of Object.keys(waitingTabs)) {
        removeListeners(ipmId);
    }
    waitingTabs = {};
    for (const [tabID, ipmId] of createdTabsMap) {
        const updateListener = onNewTabUpdateHandlerByIpmId.get(ipmId);
        if (updateListener) {
            browser.tabs.onUpdated.removeListener(updateListener);
        }
        try {
            await browser.tabs.remove(tabID);
        }
        catch (ex) {
        }
    }
    onNewTabUpdateHandlerByIpmId.clear();
    createdTabsMap.clear();
    tabIds.clear();
}
export async function start() {
    logger.debug("[new-tab]:tab manager start");
    setNewTabCommandHandler(handleCommand, clearCommands, onCommandsProcessed);
}
